import React, { useEffect, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { lazyWithRetry, resetChunkReloadGuard } from "@/lib/lazyWithRetry";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SignInReminder from "@/components/SignInReminder";
import BackButton from "@/components/BackButton";
// Eager-loaded only: HomePage (first paint critical) + a couple tiny shells
// that the global UI relies on (CommandPalette, EventOrchestrator, etc.).
// Every other route is code-split via React.lazy below so a first visit
// doesn't ship the entire app to the user's device.
import HomePage from "@/pages/HomePage";
import NotFoundPage from "@/pages/NotFoundPage";
// --- Critical AI flows: EAGERLY loaded -------------------------------------
// These pages are the highest-traffic, highest-frustration-cost surfaces on
// the site. Lazy-loading them led to blank screens in production whenever
// Emergent's deploy pipeline didn't ship the chunk file, so we bundle them
// into main.js to guarantee they're available the moment the user lands.
// Trade-off: ~80KB extra in the initial bundle in exchange for zero
// chunk-loading failure risk on the routes that matter most.
import DeepDivePage from "@/pages/DeepDivePage";
import ConnectDotsPage from "@/pages/ConnectDotsPage";
import ConnectionPage from "@/pages/ConnectionPage";
import AIDossierPage from "@/pages/AIDossierPage";
import EquationBuilderPage from "@/pages/EquationBuilderPage";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
// --- Code-split routes -----------------------------------------------------
// Lower-traffic / heavier pages stay lazy so first-paint stays small.
const ArchivePage          = lazyWithRetry(() => import(/* webpackChunkName: "p-archive" */         "@/pages/ArchivePage"));
const DossierPage          = lazyWithRetry(() => import(/* webpackChunkName: "p-dossier" */         "@/pages/DossierPage"));
const BookmarksPage        = lazyWithRetry(() => import(/* webpackChunkName: "p-bookmarks" */       "@/pages/BookmarksPage"));
// Admin pages fully removed (2026-02). Recovery tools moved to /profile.
const ThreadsPage          = lazyWithRetry(() => import(/* webpackChunkName: "p-threads" */         "@/pages/ThreadsPage"));
const ThreadDetailPage     = lazyWithRetry(() => import(/* webpackChunkName: "p-thread-detail" */   "@/pages/ThreadDetailPage"));
const MessagesPage         = lazyWithRetry(() => import(/* webpackChunkName: "p-messages" */        "@/pages/MessagesPage"));
const TrendingPage         = lazyWithRetry(() => import(/* webpackChunkName: "p-trending" */        "@/pages/TrendingPage"));
const ProfilePage          = lazyWithRetry(() => import(/* webpackChunkName: "p-profile" */         "@/pages/ProfilePage"));
const PublicProfilePage    = lazyWithRetry(() => import(/* webpackChunkName: "p-public-profile" */  "@/pages/PublicProfilePage"));
const ReceiptPage          = lazyWithRetry(() => import(/* webpackChunkName: "p-receipt" */         "@/pages/ReceiptPage"));
const WarRoomLobbyPage     = lazyWithRetry(() => import(/* webpackChunkName: "p-warroom-lobby" */   "@/pages/WarRoomLobbyPage"));
const WarRoomPage          = lazyWithRetry(() => import(/* webpackChunkName: "p-warroom" */         "@/pages/WarRoomPage"));
const WarRoomSnapshotPage  = lazyWithRetry(() => import(/* webpackChunkName: "p-warroom-snap" */    "@/pages/WarRoomSnapshotPage"));
const VaultCardsPage       = lazyWithRetry(() => import(/* webpackChunkName: "p-cards" */           "@/pages/VaultCardsPage"));
const AwakeningPage        = lazyWithRetry(() => import(/* webpackChunkName: "p-awakening" */       "@/pages/AwakeningPage"));
const AscensionTrackerPage = lazyWithRetry(() => import(/* webpackChunkName: "p-ascension" */       "@/pages/AscensionTrackerPage"));
const StitchesListPage     = lazyWithRetry(() => import(/* webpackChunkName: "p-stitches" */        "@/pages/StitchesListPage"));
const StitchDetailPage     = lazyWithRetry(() => import(/* webpackChunkName: "p-stitch-detail" */   "@/pages/StitchDetailPage"));
const StitchBuilderPage    = lazyWithRetry(() => import(/* webpackChunkName: "p-stitch-builder" */  "@/pages/StitchBuilderPage"));
const ThemeStudioPage      = lazyWithRetry(() => import(/* webpackChunkName: "p-theme-studio" */    "@/pages/ThemeStudioPage"));
const ShareThemePage       = lazyWithRetry(() => import(/* webpackChunkName: "p-share-theme" */     "@/pages/ShareThemePage"));
const SigilsPage           = lazyWithRetry(() => import(/* webpackChunkName: "p-sigils" */          "@/pages/SigilsPage"));
const BentoLayoutPage      = lazyWithRetry(() => import(/* webpackChunkName: "p-bento" */           "@/pages/BentoLayoutPage"));
const MemeStudioPage       = lazyWithRetry(() => import(/* webpackChunkName: "p-meme-studio" */     "@/pages/MemeStudioPage"));
const MemeGalleryPage      = lazyWithRetry(() => import(/* webpackChunkName: "p-meme-gallery" */    "@/pages/MemeGalleryPage"));
const TarotPage            = lazyWithRetry(() => import(/* webpackChunkName: "p-draws" */            "@/pages/DailyDrawsPage"));
const PostcardBuilderPage  = lazyWithRetry(() => import(/* webpackChunkName: "p-postcard-build" */  "@/pages/PostcardBuilderPage"));
const PostcardSharePage    = lazyWithRetry(() => import(/* webpackChunkName: "p-postcard-view" */   "@/pages/PostcardSharePage"));
const ForgePage            = lazyWithRetry(() => import(/* webpackChunkName: "p-forge" */           "@/pages/ForgePage"));
const ForgeDiscoverPage    = lazyWithRetry(() => import(/* webpackChunkName: "p-forge-disc" */     "@/pages/ForgeDiscoverPage"));
// Hidden Bosnian learning module — no nav link, discovered via /learn.
const LearnBosnianPage     = lazyWithRetry(() => import(/* webpackChunkName: "p-learn" */          "@/pages/LearnBosnianPage"));
// Hidden State Pattern Studio — no nav link, discovered via /states.
const StatesStudioPage     = lazyWithRetry(() => import(/* webpackChunkName: "p-states" */         "@/pages/StatesStudioPage"));
// Hidden Sticker Studio — no nav link, discovered via /stickers.
const StickersStudioPage   = lazyWithRetry(() => import(/* webpackChunkName: "p-stickers" */       "@/pages/StickersStudioPage"));
// Hidden Tune Studio — no nav link, discovered via /tune.
const TuneStudioPage       = lazyWithRetry(() => import(/* webpackChunkName: "p-tune" */           "@/pages/TuneStudioPage"));
// Hidden The Watch — end-times / prophecy portal, discovered via /watch.
const WatchPage            = lazyWithRetry(() => import(/* webpackChunkName: "p-watch" */          "@/pages/WatchPage"));
import CommandPalette from "@/components/CommandPalette";
import EventOrchestrator from "@/components/EventOrchestrator";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import { VaultThemeProvider } from "@/components/themes/VaultThemeProvider";
import ThemeStage from "@/components/themes/ThemeStage";
import "@/styles/themes.css";
import { AuthProvider } from "@/lib/auth";
import { markVisitNow } from "@/lib/lastVisit";
import { toast } from "sonner";

// Lightweight Suspense fallback while the per-route chunk downloads. Keeps
// the navbar/footer/back button up so the user sees the shell instantly.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-fallback">
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-8 h-8 border-2 border-[#ff2a8e]/30 border-t-[#ff2a8e] rounded-full animate-spin"
          aria-hidden="true"
        />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
          // DECRYPTING FILE
        </div>
      </div>
    </div>
  );
}

function App() {
  // Stamp this session for the "NEW SINCE LAST VISIT" badges. Idempotent within a session;
  // only the first call across a new session (>30min gap) updates lastVisit.
  useEffect(() => {
    markVisitNow();
    // Reset the one-shot chunk-load reload guard now that the app has booted
    // successfully — so if a NEW chunk failure happens later in this session,
    // lazyWithRetry's one-time reload escape hatch is available again.
    resetChunkReloadGuard();
  }, []);
  // Detect when the app is running as an installed PWA (standalone display
  // mode) and stamp the <html> with a data-attribute so CSS / components can
  // hide things like the "Made with Emergent" badge that don't belong inside
  // a native-feeling shell.
  useEffect(() => {
    const apply = () => {
      try {
        const isStandalone =
          window.matchMedia?.("(display-mode: standalone)").matches ||
          window.navigator?.standalone === true ||
          !!window.Capacitor;
        document.documentElement.setAttribute(
          "data-vault-standalone",
          isStandalone ? "on" : "off"
        );
      } catch { /* ignore */ }
    };
    apply();
    const mql = window.matchMedia?.("(display-mode: standalone)");
    mql?.addEventListener?.("change", apply);
    return () => mql?.removeEventListener?.("change", apply);
  }, []);
  // Surface a toast when a new service worker version has been installed —
  // user can refresh to grab the latest bundle.
  useEffect(() => {
    const onUpdate = () => {
      toast("New version available", {
        description: "Tap to reload and get the latest XYX build.",
        duration: 12000,
        action: { label: "Reload", onClick: () => window.location.reload() },
      });
    };
    window.addEventListener("vault:sw-update-ready", onUpdate);
    return () => window.removeEventListener("vault:sw-update-ready", onUpdate);
  }, []);
  return (
    <div className="App min-h-screen bg-[#050505] text-[#f5f5f5]">
      <AuthProvider>
        <VaultThemeProvider>
          <BrowserRouter>
            <ThemeStage />
            <Navbar />
          <SignInReminder />
          <div className="vault-global-back-btn max-w-screen-2xl mx-auto px-4 sm:px-8 pt-3 pb-1">
            <BackButton />
          </div>
          <main>
            <RouteErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/archive" element={<ArchivePage />} />
              <Route path="/dossier/:slug" element={<DossierPage />} />
              <Route path="/ai-dossier/:id" element={<AIDossierPage />} />
              <Route path="/deep-dive" element={<DeepDivePage />} />
              <Route path="/bookmarks" element={<BookmarksPage />} />
              <Route path="/connect-dots" element={<ConnectDotsPage />} />
              <Route path="/connection/:id" element={<ConnectionPage />} />
              <Route path="/equation" element={<EquationBuilderPage />} />
              <Route path="/threads" element={<ThreadsPage />} />
              <Route path="/threads/messages" element={<MessagesPage />} />
              <Route path="/threads/messages/:peerId" element={<MessagesPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/messages/:peerId" element={<MessagesPage />} />
              <Route path="/threads/:threadId" element={<ThreadDetailPage />} />
              <Route path="/trending" element={<TrendingPage />} />
              <Route path="/vault-cards" element={<VaultCardsPage />} />
              <Route path="/awakening" element={<AwakeningPage />} />
              <Route path="/ascension" element={<AscensionTrackerPage />} />
              <Route path="/stitches" element={<StitchesListPage />} />
              <Route path="/stitches/new" element={<StitchBuilderPage />} />
              <Route path="/stitches/:stitchId" element={<StitchDetailPage />} />
              <Route path="/stitches/:stitchId/edit" element={<StitchBuilderPage />} />
              <Route path="/warroom" element={<WarRoomLobbyPage />} />
              <Route path="/warroom/snapshot/:snapId" element={<WarRoomSnapshotPage />} />
              <Route path="/warroom/:id" element={<WarRoomPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/u/:username" element={<PublicProfilePage />} />
              <Route path="/r/:username/:year" element={<ReceiptPage />} />
              <Route path="/r/:username/:year/:month" element={<ReceiptPage />} />
              <Route path="/theme-studio" element={<ThemeStudioPage />} />
              <Route path="/share/theme/:username/:slotId" element={<ShareThemePage mode="theme" />} />
              <Route path="/share/mix/:username/:mixId" element={<ShareThemePage mode="mix" />} />
              <Route path="/sigils" element={<SigilsPage />} />
              <Route path="/bento-layout" element={<BentoLayoutPage />} />
              <Route path="/meme-studio" element={<MemeStudioPage />} />
              <Route path="/meme-studio/:memeId" element={<MemeStudioPage />} />
              <Route path="/gallery/memes" element={<MemeGalleryPage />} />
              <Route path="/soul-static" element={<TarotPage />} />
              <Route path="/tarot" element={<TarotPage />} />
              <Route path="/draws" element={<TarotPage />} />
              <Route path="/postcards" element={<PostcardBuilderPage />} />
              <Route path="/postcard/:token" element={<PostcardSharePage />} />
              <Route path="/forge" element={<ForgePage />} />
              <Route path="/forge/discover" element={<ForgeDiscoverPage />} />
              <Route path="/forge/public/:deckId" element={<ForgeDiscoverPage />} />
              <Route path="/forge/:deckId" element={<ForgePage />} />
              {/* Hidden Bosnian language learning module — no nav link. */}
              <Route path="/learn" element={<LearnBosnianPage />} />
              {/* Hidden State Pattern Studio — no nav link. */}
              <Route path="/states" element={<StatesStudioPage />} />
              {/* Hidden Sticker Studio — no nav link. */}
              <Route path="/stickers" element={<StickersStudioPage />} />
              <Route path="/tune" element={<TuneStudioPage />} />
              <Route path="/watch" element={<WatchPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
          </main>
          <Footer />
          <CommandPalette />
          <EventOrchestrator />
          <PwaInstallPrompt />
          </BrowserRouter>
        </VaultThemeProvider>
      </AuthProvider>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
